/**
 * IndexedDB 기반 오프라인 사진 업로드 큐 (P2-8).
 *
 * 의존성 0 (raw IndexedDB API + Promise wrapper).
 *
 * DB schema:
 *   `seolhwa-offline` DB → `photo_queue` ObjectStore
 *     keyPath: id (auto-increment number)
 *     index: project_id
 *     fields: { project_id, filename, blob, added_at, last_error?, retry_count }
 *
 * 사용:
 *   await enqueuePhoto({ projectId, filename, blob })
 *   const items = await getQueuedPhotos()
 *   await removeFromQueue(item.id)
 *   await markRetry(item.id, errorMsg)
 *
 * 동시성: IndexedDB transaction 은 본질적으로 원자 — 동시 호출 시
 * 마지막 commit 만 살아남음. 대량 동시 enqueue 가 없으면 충분.
 */

const DB_NAME = 'seolhwa-offline'
const DB_VERSION = 1
const STORE = 'photo_queue'

export interface QueuedPhoto {
  id: number
  project_id: string
  filename: string
  blob: Blob
  added_at: number      // epoch ms
  last_error?: string
  retry_count: number
}

let _dbPromise: Promise<IDBDatabase> | null = null

function openDb(): Promise<IDBDatabase> {
  if (_dbPromise) return _dbPromise
  _dbPromise = new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, {
          keyPath: 'id',
          autoIncrement: true,
        })
        store.createIndex('project_id', 'project_id', { unique: false })
        store.createIndex('added_at', 'added_at', { unique: false })
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
  return _dbPromise
}

function tx(mode: IDBTransactionMode): Promise<IDBObjectStore> {
  return openDb().then((db) => {
    const t = db.transaction(STORE, mode)
    return t.objectStore(STORE)
  })
}

export async function enqueuePhoto(args: {
  projectId: string
  filename: string
  blob: Blob
}): Promise<number> {
  const store = await tx('readwrite')
  return new Promise<number>((resolve, reject) => {
    const req = store.add({
      project_id: args.projectId,
      filename: args.filename,
      blob: args.blob,
      added_at: Date.now(),
      retry_count: 0,
    })
    req.onsuccess = () => resolve(req.result as number)
    req.onerror = () => reject(req.error)
  })
}

export async function getQueuedPhotos(projectId?: string): Promise<QueuedPhoto[]> {
  const store = await tx('readonly')
  return new Promise<QueuedPhoto[]>((resolve, reject) => {
    const out: QueuedPhoto[] = []
    const req = projectId
      ? store.index('project_id').openCursor(IDBKeyRange.only(projectId))
      : store.openCursor()
    req.onsuccess = () => {
      const cursor = req.result
      if (cursor) {
        out.push(cursor.value as QueuedPhoto)
        cursor.continue()
      } else {
        // added_at 오름차순 (먼저 들어온 것 먼저 업로드)
        out.sort((a, b) => a.added_at - b.added_at)
        resolve(out)
      }
    }
    req.onerror = () => reject(req.error)
  })
}

export async function removeFromQueue(id: number): Promise<void> {
  const store = await tx('readwrite')
  return new Promise<void>((resolve, reject) => {
    const req = store.delete(id)
    req.onsuccess = () => resolve()
    req.onerror = () => reject(req.error)
  })
}

export async function markRetry(id: number, errorMsg: string): Promise<void> {
  const store = await tx('readwrite')
  return new Promise<void>((resolve, reject) => {
    const getReq = store.get(id)
    getReq.onsuccess = () => {
      const item = getReq.result as QueuedPhoto | undefined
      if (!item) {
        resolve()
        return
      }
      item.last_error = errorMsg.slice(0, 300)
      item.retry_count = (item.retry_count ?? 0) + 1
      const putReq = store.put(item)
      putReq.onsuccess = () => resolve()
      putReq.onerror = () => reject(putReq.error)
    }
    getReq.onerror = () => reject(getReq.error)
  })
}

export async function queueCount(projectId?: string): Promise<number> {
  const items = await getQueuedPhotos(projectId)
  return items.length
}
